import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Account, Invoice, Expense } from '../types';
import { DollarSign, FileText, TrendingUp, Wallet, Calendar, Download, Loader2, BarChart3, ChevronDown } from 'lucide-react';
import { downloadFile } from '../lib/downloadHelper';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { collection, query, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'sonner';

const safeGetDate = (val: any): Date => {
  if (!val) return new Date(0);
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? new Date(0) : d;
};

interface AccountingReportsProps {
  accounts: Account[];
  invoices: Invoice[];
}

export interface AccountingReportsRef {
  generateTrialBalance: (from: string, to: string) => Promise<jsPDF>;
  generateProfitLoss: (from: string, to: string) => Promise<jsPDF>;
  generateBalanceSheet: (to: string) => Promise<jsPDF>;
  generateLedger: (from: string, to: string) => Promise<jsPDF>;
  generateFinancial: (from: string, to: string, sections: string[]) => Promise<jsPDF>;
}

const AccountingReports = forwardRef<AccountingReportsRef, AccountingReportsProps>(({ accounts, invoices }, ref) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  // Local state for dashboard display, but methods will take params for generation
  const [displayDateFrom, setDisplayDateFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [displayDateTo, setDisplayDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [letterheadUrl, setLetterheadUrl] = useState<string | undefined>(undefined);

  useImperativeHandle(ref, () => ({
    generateTrialBalance: async (from, to) => await generateTrialBalanceReport(from, to),
    generateProfitLoss: async (from, to) => await generateProfitLossReport(from, to),
    generateBalanceSheet: async (to) => await generateBalanceSheetReport(to),
    generateLedger: async (from, to) => await generateLedgerReport(from, to),
    generateFinancial: async (from, to, sections) => await generateFinancialReport(from, to, sections),
  }));

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'expenses'), (snapshot) => {
      setExpenses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Expense)));
    });

    const unsubscribeSettings = onSnapshot(query(collection(db, 'settings'), limit(1)), (snapshot) => {
      if (!snapshot.empty) {
        setLetterheadUrl(snapshot.docs[0].data().letterheadUrl);
      }
    });

    return () => {
      unsubscribe();
      unsubscribeSettings();
    };
  }, []);

  const totalBalance = accounts.reduce((sum, acc) => sum + (Number(acc.balance) || 0), 0);
  const totalInvoiced = invoices.reduce((sum, inv) => sum + (Number(inv.totalAmount) || 0), 0);
  const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (Number(inv.totalAmount) || 0), 0);

  const generateTrialBalanceReport = async (from: string, to: string): Promise<jsPDF> => {
    setIsGenerating(true);
    try {
      const doc = new jsPDF();
      const end = new Date(to);
      end.setHours(23, 59, 59, 999);

      // Header
      if (letterheadUrl) {
        try {
          doc.addImage(letterheadUrl, 'PNG', 10, 5, 190, 35);
        } catch (e) {
          doc.setFillColor(30, 64, 175);
          doc.rect(0, 0, 210, 40, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(24);
          doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
        }
      } else {
        doc.setFillColor(30, 64, 175);
        doc.rect(0, 0, 210, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
      }

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('TRIAL BALANCE', 105, 55, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`As of ${end.toLocaleDateString()}`, 105, 62, { align: 'center' });

      const trialBalanceData = accounts.map(acc => {
        // Simple logic: assets/expenses = debit, revenue/liabilities/equity = credit
        const isDebit = ['bank', 'cash', 'Mobile Money', 'Fixed Assets', 'Assets'].includes(acc.type);
        return [
          acc.name,
          isDebit ? acc.balance.toLocaleString() : '-',
          !isDebit ? acc.balance.toLocaleString() : '-'
        ];
      });

      const totalDebit = accounts
        .filter(acc => ['bank', 'cash', 'Mobile Money', 'Fixed Assets', 'Assets'].includes(acc.type))
        .reduce((sum, acc) => sum + acc.balance, 0);
      
      const totalCredit = accounts
        .filter(acc => !['bank', 'cash', 'Mobile Money', 'Fixed Assets', 'Assets'].includes(acc.type))
        .reduce((sum, acc) => sum + acc.balance, 0);

      autoTable(doc, {
        startY: 75,
        head: [['Account Name', 'Debit (TZS)', 'Credit (TZS)']],
        body: [
          ...trialBalanceData,
          [{ content: 'TOTAL', styles: { fontStyle: 'bold' } }, { content: totalDebit.toLocaleString(), styles: { fontStyle: 'bold' } }, { content: totalCredit.toLocaleString(), styles: { fontStyle: 'bold' } }]
        ],
        theme: 'grid',
        headStyles: { fillColor: [30, 64, 175], textColor: [255, 255, 255] },
        styles: { fontSize: 10, cellPadding: 5 }
      });

      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 285, { align: 'center' });
      return doc;
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate report');
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const generateFinancialReport = async (from: string, to: string, sections: string[]): Promise<jsPDF> => {
    setIsGenerating(true);
    try {
      const doc = new jsPDF();
      const start = new Date(from);
      const end = new Date(to);
      end.setHours(23, 59, 59, 999);

      const filteredInvoices = invoices.filter(inv => {
        const d = safeGetDate(inv.createdAt);
        return d && d >= start && d <= end;
      });

      const filteredExpenses = expenses.filter(exp => {
        const d = safeGetDate(exp.date);
        return d && d >= start && d <= end;
      });

      const revenue = filteredInvoices.reduce((sum, inv) => sum + (Number(inv.totalAmount) || 0), 0);
      const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);
      
      const taxes = filteredExpenses.filter(e => e.category === 'Taxes' || e.category === 'Tax').reduce((sum, e) => sum + e.amount, 0);
      const adminCosts = filteredExpenses.filter(e => e.category === 'Administration').reduce((sum, e) => sum + e.amount, 0);
      const opsCosts = filteredExpenses.filter(e => e.category === 'Operations').reduce((sum, e) => sum + e.amount, 0);
      
      const profitBeforeTax = revenue - (totalExpenses - taxes);
      const profitAfterTax = profitBeforeTax - taxes;

      // Assets
      const currentAssets = accounts.filter(a => ['bank', 'cash', 'Mobile Money'].includes(a.type)).reduce((sum, a) => sum + a.balance, 0);
      const receivables = filteredInvoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + i.totalAmount, 0);
      const totalCurrentAssets = currentAssets + receivables;
      
      const nonCurrentAssets = accounts.filter(a => a.type === 'Fixed Assets').reduce((sum, a) => sum + a.balance, 0);
      
      // Liabilities
      const currentLiabilities = accounts.filter(a => a.type === 'Current Liabilities').reduce((sum, a) => sum + a.balance, 0);
      const nonCurrentLiabilities = accounts.filter(a => a.type === 'Non-Current Liabilities').reduce((sum, a) => sum + a.balance, 0);

      // Header
      if (letterheadUrl) {
        try {
          doc.addImage(letterheadUrl, 'PNG', 10, 5, 190, 35);
        } catch (e) {
          doc.setFillColor(30, 64, 175);
          doc.rect(0, 0, 210, 40, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(24);
          doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
        }
      } else {
        doc.setFillColor(30, 64, 175);
        doc.rect(0, 0, 210, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
      }

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('FINANCIAL SUMMARY REPORT', 105, 55, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Period: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`, 105, 62, { align: 'center' });

      const reportBody: any[] = [];

      if (sections.includes('revenue')) {
        reportBody.push([{ content: 'REVENUE & EXPENDITURE', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } }]);
        reportBody.push(['Revenue', 'Total Billing Revenue', revenue.toLocaleString()]);
        reportBody.push(['Expenses', 'Total Operational & Admin Costs', totalExpenses.toLocaleString()]);
        reportBody.push(['Administration cost', 'Office & Admin Expenses', adminCosts.toLocaleString()]);
        reportBody.push(['Operational cost', 'Technical & Operations', opsCosts.toLocaleString()]);
        reportBody.push(['Tax paid', 'Government Taxes', taxes.toLocaleString()]);
      }

      if (sections.includes('profitability')) {
        reportBody.push([{ content: 'PROFITABILITY', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } }]);
        reportBody.push(['Profit before tax', '', profitBeforeTax.toLocaleString()]);
        reportBody.push(['Profit after Tax', '', profitAfterTax.toLocaleString()]);
      }

      if (sections.includes('assets')) {
        reportBody.push([{ content: 'ASSETS', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } }]);
        reportBody.push(['Current Assets', 'Cash, Bank & Receivables', totalCurrentAssets.toLocaleString()]);
        reportBody.push(['Non-current Assets', 'Fixed Assets & Equipment', nonCurrentAssets.toLocaleString()]);
      }

      if (sections.includes('liabilities')) {
        reportBody.push([{ content: 'LIABILITIES', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } }]);
        reportBody.push(['Current Liabilities', 'Payables', currentLiabilities.toLocaleString()]);
        reportBody.push(['Non-Current Liability', 'Long Term Debts', nonCurrentLiabilities.toLocaleString()]);
      }

      autoTable(doc, {
        startY: 75,
        head: [['SECTION', 'DETAILS', 'AMOUNT (TZS)']],
        body: reportBody,
        theme: 'grid',
        headStyles: { fillColor: [30, 64, 175], textColor: [255, 255, 255] },
        styles: { fontSize: 10, cellPadding: 5 }
      });

      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 285, { align: 'center' });
      return doc;
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate report');
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const generateProfitLossReport = async (from: string, to: string): Promise<jsPDF> => {
    setIsGenerating(true);
    try {
      const doc = new jsPDF();
      const start = new Date(from);
      const end = new Date(to);
      end.setHours(23, 59, 59, 999);

      const filteredInvoices = invoices.filter(inv => {
        const d = safeGetDate(inv.createdAt);
        return d && d >= start && d <= end;
      });

      const filteredExpenses = expenses.filter(exp => {
        const d = safeGetDate(exp.date);
        return d && d >= start && d <= end;
      });

      const revenue = filteredInvoices.reduce((sum, inv) => sum + (Number(inv.totalAmount) || 0), 0);
      const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);
      const netProfit = revenue - totalExpenses;

      // Header
      if (letterheadUrl) {
        try {
          doc.addImage(letterheadUrl, 'PNG', 10, 5, 190, 35);
        } catch (e) {
          doc.setFillColor(30, 64, 175);
          doc.rect(0, 0, 210, 40, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(24);
          doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
        }
      } else {
        doc.setFillColor(30, 64, 175);
        doc.rect(0, 0, 210, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
      }

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('PROFIT & LOSS STATEMENT', 105, 55, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Period: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`, 105, 62, { align: 'center' });

      autoTable(doc, {
        startY: 75,
        head: [['Description', 'Amount (TZS)']],
        body: [
          [{ content: 'REVENUE', styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } }, ''],
          ['Total Invoiced Revenue', revenue.toLocaleString()],
          ['', ''],
          [{ content: 'EXPENSES', styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } }, ''],
          ...Object.entries(filteredExpenses.reduce((acc, exp) => {
            acc[exp.category] = (acc[exp.category] || 0) + Number(exp.amount);
            return acc;
          }, {} as Record<string, number>)).map(([cat, amt]) => [`  ${cat}`, amt.toLocaleString()]),
          [{ content: 'TOTAL EXPENSES', styles: { fontStyle: 'bold' } }, { content: totalExpenses.toLocaleString(), styles: { fontStyle: 'bold' } }],
          ['', ''],
          [{ content: 'NET PROFIT / (LOSS)', styles: { fontStyle: 'bold', fillColor: [30, 64, 175], textColor: [255, 255, 255] } }, { content: netProfit.toLocaleString(), styles: { fontStyle: 'bold', fillColor: [30, 64, 175], textColor: [255, 255, 255] } }],
        ],
        theme: 'grid',
        headStyles: { fillColor: [30, 64, 175], textColor: [255, 255, 255] },
        styles: { fontSize: 10, cellPadding: 5 }
      });

      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 285, { align: 'center' });
      return doc;
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate report');
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const generateBalanceSheetReport = async (asOf: string): Promise<jsPDF> => {
    setIsGenerating(true);
    try {
      const doc = new jsPDF();
      
      // Header
      if (letterheadUrl) {
        try {
          doc.addImage(letterheadUrl, 'PNG', 10, 5, 190, 35);
        } catch (e) {
          doc.setFillColor(30, 64, 175);
          doc.rect(0, 0, 210, 40, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(24);
          doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
        }
      } else {
        doc.setFillColor(30, 64, 175);
        doc.rect(0, 0, 210, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
      }

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('BALANCE SHEET', 105, 55, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const asOfDate = new Date(asOf);
      asOfDate.setHours(23, 59, 59, 999);
      doc.text(`As of ${asOfDate.toLocaleDateString()}`, 105, 62, { align: 'center' });

      // Filter data up to the asOfDate
      const filteredInvoices = invoices.filter(i => {
        const d = safeGetDate(i.createdAt);
        return d && d <= asOfDate;
      });

      const filteredExpenses = expenses.filter(e => {
        const d = safeGetDate(e.date);
        return d && d <= asOfDate;
      });

      const assets = accounts.filter(a => ['bank', 'cash', 'Mobile Money', 'Fixed Assets', 'Assets'].includes(a.type));
      const currentAssets = accounts.filter(a => ['bank', 'cash', 'Mobile Money'].includes(a.type));
      const totalCurrentAssetValue = currentAssets.reduce((sum, a) => sum + a.balance, 0);
      
      const fixedAssets = accounts.filter(a => ['Fixed Assets', 'Assets'].includes(a.type));
      const totalFixedAssetValue = fixedAssets.reduce((sum, a) => sum + a.balance, 0);
      
      const receivables = filteredInvoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + i.totalAmount, 0);
      const totalAssets = totalCurrentAssetValue + totalFixedAssetValue + receivables;

      const liabilities = accounts.filter(a => ['Current Liabilities', 'Non-Current Liabilities'].includes(a.type));
      const totalLiabilities = liabilities.reduce((sum, a) => sum + a.balance, 0);

      const equity = accounts.filter(a => ['Equity'].includes(a.type));
      const totalEquity = equity.reduce((sum, a) => sum + a.balance, 0);

      // Simple accounting equation check: Assets = Liabilities + Equity
      // Retained Earnings is essentially the balancing figure here for this simple app
      const retainedEarnings = totalAssets - totalLiabilities - totalEquity;

      autoTable(doc, {
        startY: 75,
        head: [['SECTION', 'DETAILS', 'AMOUNT (TZS)']],
        body: [
          [{ content: 'ASSETS', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } }],
          [{ content: 'Current Assets', colSpan: 2, styles: { fontStyle: 'bold' } }, ''],
          ...currentAssets.map(a => ['', a.name, a.balance.toLocaleString()]),
          ['', 'Accounts Receivable', receivables.toLocaleString()],
          [{ content: 'Non-Current Assets', colSpan: 2, styles: { fontStyle: 'bold' } }, ''],
          ...fixedAssets.map(a => ['', a.name, a.balance.toLocaleString()]),
          [{ content: 'TOTAL ASSETS', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [30, 64, 175], textColor: [255, 255, 255] } }, { content: totalAssets.toLocaleString(), styles: { fontStyle: 'bold', fillColor: [30, 64, 175], textColor: [255, 255, 255] } }],
          ['', '', ''],
          [{ content: 'LIABILITIES & EQUITY', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } }],
          [{ content: 'Liabilities', colSpan: 2, styles: { fontStyle: 'bold' } }, ''],
          ...liabilities.map(a => ['', a.name, a.balance.toLocaleString()]),
          [{ content: 'Equity', colSpan: 2, styles: { fontStyle: 'bold' } }, ''],
          ...equity.map(a => ['', a.name, a.balance.toLocaleString()]),
          ['', 'Retained Earnings (Period)', retainedEarnings.toLocaleString()],
          [{ content: 'TOTAL LIABILITIES & EQUITY', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [30, 64, 175], textColor: [255, 255, 255] } }, { content: totalAssets.toLocaleString(), styles: { fontStyle: 'bold', fillColor: [30, 64, 175], textColor: [255, 255, 255] } }],
        ],
        theme: 'grid',
        headStyles: { fillColor: [30, 64, 175], textColor: [255, 255, 255] },
        styles: { fontSize: 10, cellPadding: 5 }
      });

      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 285, { align: 'center' });
      return doc;
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate report');
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const generateLedgerReport = async (from: string, to: string): Promise<jsPDF> => {
    setIsGenerating(true);
    try {
      const doc = new jsPDF();
      const start = new Date(from);
      const end = new Date(to);
      end.setHours(23, 59, 59, 999);

      // Header
      if (letterheadUrl) {
        try {
          doc.addImage(letterheadUrl, 'PNG', 10, 5, 190, 35);
        } catch (e) {
          doc.setFillColor(30, 64, 175);
          doc.rect(0, 0, 210, 40, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(24);
          doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
        }
      } else {
        doc.setFillColor(30, 64, 175);
        doc.rect(0, 0, 210, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
      }

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('GENERAL LEDGER REPORT', 105, 55, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Period: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`, 105, 62, { align: 'center' });

      let currentY = 75;

      accounts.forEach((account, index) => {
        if (index > 0) {
          doc.addPage();
          currentY = 20;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(`Account: ${account.name} (${account.type})`, 20, currentY);
        
        const accountExpenses = expenses.filter(e => e.accountId === account.id && safeGetDate(e.date) >= start && safeGetDate(e.date) <= end);
        
        // For a simple app, we'll attribute all invoice income to the first Asset account if no specific link exists
        // Or if the account type is 'bank' or 'cash' and it's the first one, we show the income there.
        const isDefaultIncomeAccount = index === accounts.findIndex(a => ['bank', 'cash', 'Mobile Money'].includes(a.type));
        const accountInvoices = isDefaultIncomeAccount 
          ? invoices.filter(i => i.status === 'paid' && safeGetDate(i.createdAt) >= start && safeGetDate(i.createdAt) <= end)
          : [];

        const ledgerEntries = [
          ...accountExpenses.map(e => ({
            date: safeGetDate(e.date),
            description: e.description,
            type: 'Expense',
            debit: e.amount,
            credit: 0
          })),
          ...accountInvoices.map(i => ({
            date: safeGetDate(i.createdAt),
            description: `Invoice ${i.invoiceNumber} - ${i.clientName}`,
            type: 'Income',
            debit: 0,
            credit: i.totalAmount
          }))
        ].sort((a, b) => a.date.getTime() - b.date.getTime());

        autoTable(doc, {
          startY: currentY + 5,
          head: [['Date', 'Description', 'Type', 'Debit', 'Credit']],
          body: ledgerEntries.map(e => [
            e.date.toLocaleDateString(),
            e.description,
            e.type,
            e.debit > 0 ? e.debit.toLocaleString() : '-',
            e.credit > 0 ? e.credit.toLocaleString() : '-'
          ]),
          theme: 'grid',
          headStyles: { fillColor: [71, 85, 105], textColor: [255, 255, 255] },
          styles: { fontSize: 9 }
        });

        currentY = (doc as any).lastAutoTable.finalY + 15;
      });

      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 285, { align: 'center' });
      return doc;
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate report');
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-slate-900">Accounting Reports</h2>
        <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={displayDateFrom}
              onChange={(e) => setDisplayDateFrom(e.target.value)}
              className="text-sm border-none focus:ring-0 bg-transparent font-bold text-slate-600"
            />
          </div>
          <span className="text-slate-300">to</span>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={displayDateTo}
              onChange={(e) => setDisplayDateTo(e.target.value)}
              className="text-sm border-none focus:ring-0 bg-transparent font-bold text-slate-600"
            />
          </div>
          <div className="h-8 w-px bg-slate-100 mx-2" />
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-800 rounded-xl text-[10px] font-black uppercase tracking-widest">
            <BarChart3 className="w-3 h-3" />
            Reporting Mode
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Wallet className="w-6 h-6 text-blue-800" />
            <h3 className="font-bold text-slate-900">Total Balance</h3>
          </div>
          <p className="text-3xl font-black text-blue-800">{totalBalance.toLocaleString()}</p>
        </div>
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-6 h-6 text-orange-500" />
            <h3 className="font-bold text-slate-900">Total Invoiced</h3>
          </div>
          <p className="text-3xl font-black text-orange-500">{totalInvoiced.toLocaleString()}</p>
        </div>
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-6 h-6 text-emerald-600" />
            <h3 className="font-bold text-slate-900">Total Paid</h3>
          </div>
          <p className="text-3xl font-black text-emerald-600">{totalPaid.toLocaleString()}</p>
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
        <h3 className="font-bold text-slate-900 mb-4">Ledger Account Report</h3>
        <table className="w-full">
          <thead>
            <tr className="text-left text-slate-500 text-xs uppercase">
              <th className="pb-4">Account</th>
              <th className="pb-4">Type</th>
              <th className="pb-4">Balance</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(acc => (
              <tr key={acc.id} className="border-t border-slate-100">
                <td className="py-4 font-bold text-slate-900">{acc.name}</td>
                <td className="py-4 text-slate-600">{acc.type}</td>
                <td className="py-4 font-bold text-blue-800">{acc.balance.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default AccountingReports;
